import Project from './models/Project';
import { isAudioFile } from './utils';
import { startBulkUpload } from './uploads';

function fileFromEntry(entry) {
    return new Promise((resolve) => {
        entry.file((f) => resolve(f), () => resolve(null));
    });
}

// Recursively walks a directory entry. Returns every audio File beneath it
// (`allAudio`), the audio sitting directly in this folder (`directAudio`),
// and one "leaf group" per folder that directly holds audio (`leafGroups`) —
// the granularity used when the user asks for separate projects.
async function walkDirectory(dirEntry) {
    const reader = dirEntry.createReader();
    const fileEntries = [];
    const subDirs = [];
    // readEntries is paginated — keep calling until it returns empty.
    while (true) {
        const batch = await new Promise((resolve) => {
            reader.readEntries(resolve, () => resolve([]));
        });
        if (!batch.length) break;
        for (const entry of batch) {
            if (entry.isDirectory) subDirs.push(entry);
            else if (entry.isFile) fileEntries.push(entry);
        }
    }

    const directAudio = (await Promise.all(fileEntries.map(fileFromEntry)))
        .filter(isAudioFile);

    const leafGroups = directAudio.length
        ? [{ name: dirEntry.name, files: directAudio }]
        : [];
    const allAudio = [...directAudio];

    for (const r of await Promise.all(subDirs.map(walkDirectory))) {
        allAudio.push(...r.allAudio);
        leafGroups.push(...r.leafGroups);
    }
    return { allAudio, directAudio, leafGroups };
}

// Splits a drop into:
//   topGroups    — one per top-level dropped folder, all audio beneath it
//   folderGroups — one per leaf folder (the folder that directly holds audio)
//   looseFiles   — audio dropped outside any folder
//   isNested     — true when any dropped folder contains a sub-folder of audio
//   hasFolders   — true when at least one folder of audio was dropped
//
// The DataTransferItemList is invalidated once the drop handler returns, so
// `webkitGetAsEntry()` is called synchronously up front before any await.
export async function extractFolderDrop(dataTransfer) {
    const items = dataTransfer?.items;
    const entries = [];
    if (items) {
        for (let i = 0; i < items.length; i++) {
            const it = items[i];
            if (it.kind !== 'file') continue;
            const entry = it.webkitGetAsEntry?.();
            if (entry) entries.push(entry);
        }
    }

    if (!entries.length) {
        const loose = dataTransfer?.files ? Array.from(dataTransfer.files) : [];
        return {
            topGroups: [],
            folderGroups: [],
            looseFiles: loose.filter(isAudioFile),
            isNested: false,
            hasFolders: false,
        };
    }

    const looseFiles = (await Promise.all(
        entries.filter((e) => e.isFile).map(fileFromEntry),
    )).filter(isAudioFile);

    const topGroups = [];
    const folderGroups = [];
    let isNested = false;

    const dirs = entries.filter((e) => e.isDirectory);
    const walked = await Promise.all(dirs.map(walkDirectory));
    dirs.forEach((dir, i) => {
        const r = walked[i];
        if (!r.allAudio.length) return;
        topGroups.push({ id: `t${topGroups.length}`, name: dir.name, files: r.allAudio });
        for (const lg of r.leafGroups) {
            folderGroups.push({ id: `f${folderGroups.length}`, name: lg.name, files: lg.files });
        }
        if (r.allAudio.length > r.directAudio.length) isNested = true;
    });

    return {
        topGroups,
        folderGroups,
        looseFiles,
        isNested,
        hasFolders: topGroups.length > 0,
    };
}

// One new project per group, each named after its folder. The track count is
// set optimistically so the sidebar is correct before uploads finish.
export async function createSeparateProjects(artist, groups) {
    return Promise.all(groups.map(async (group) => {
        const project = await Project.create(artist, group.name);
        project.numberOfTracks = group.files.length;
        await startBulkUpload(project, group.files);
        return project;
    }));
}

// Shared by both drop targets — creates the projects, reports them up, and
// owns the error path so the panels don't each repeat it.
export async function importGroupsAsProjects(artist, groups, onProjectsCreated) {
    try {
        onProjectsCreated?.(await createSeparateProjects(artist, groups));
    } catch (err) {
        console.error('Failed to create projects from folders', err);
        alert('Could not create projects from the dropped folders.');
    }
}
